import {
  createHttpClient,
  devTenantHeaders,
  ErrorCodes,
  isApiError,
  normalizeAuthId,
  type RequestOptions,
  type WireAuthId,
} from '@erp/shared'
import router from '@/router'
import {
  accessTokenExpiringWithin,
  bearerHeader,
  clearTokens,
  currentSession,
  hasSession,
  onAuthChanged,
  refreshTokenOrNull,
  saveTokens,
  UNAUTHORIZED_REDIRECT_KEY,
} from './tokens'

type Identity = string | null
class SessionChangedError extends Error {}
const identityOf = (): Identity => {
  const session = currentSession()
  return session ? JSON.stringify([session.subjectType, session.userId, session.tenantId, session.subjectId]) : null
}

// Login/logout changes the request owner; a refresh rotation by this client does not.
let authEpoch = 0
let savingRefreshToken: string | null = null
onAuthChanged(() => {
  if (savingRefreshToken === null || refreshTokenOrNull() !== savingRefreshToken) authEpoch++
})
type RequestOwner = { identity: Identity; epoch: number }
const ownerNow = (): RequestOwner => ({ identity: identityOf(), epoch: authEpoch })
const sameOwner = (owner: RequestOwner): boolean => owner.epoch === authEpoch && owner.identity === identityOf()
function requireOwner(owner: RequestOwner): void {
  if (!sameOwner(owner)) throw new SessionChangedError('登录身份已变化，请在原账号核对请求结果，不要直接重发')
}

/**
 * 卖家端唯一的 HTTP 客户端实例。
 *
 * `baseUrl` 留空：
 * - 开发期由 Vite 代理把 `/api/**` 转给 `localhost:8080`（同源，不需要 CORS）；
 * - 生产由 k8s Ingress 同域分流（同样同源）。
 * ⇒ 两个环境的请求路径**完全一致**，没有「本地能跑上线不能跑」的余地。
 *
 * <h2>🔴🔴 `extraHeaders` 是整个应用里唯一注入开发期租户头的地方</h2>
 * 三元表达式的两边都要看：
 * - `import.meta.env.DEV` 是 Vite 的**构建期常量**，生产构建里折叠成 `false`；
 * - ⇒ 生产产物里既不会调 `devTenantHeaders`，也不会保留对它的引用
 *   （实测：`dist/**.js` 里 grep 不到 `X-Tenant-Id`）。
 *
 * ⛔ 不要把它改成运行时开关（读 env 变量 / 读 localStorage）——
 * 那样这段代码就**留在生产里**了，而它的作用是「任何人都能指定自己是哪个租户」。
 *
 * 登录接口接上之后：这一行连同 `shared/src/http/devTenant.ts` 一起删。
 */
/**
 * 🔴 请求头的取法有**优先级**，而且顺序是刻意的：
 *
 *   ① 有会话 ⇒ 带 `Authorization: Bearer …`，**⛔ 不再附加开发期租户头**；
 *   ② 没会话 ⇒ 开发构建里退回开发期租户头（让还没接登录的页面继续能用）。
 *
 * ⚠️ 两个**都带**是最危险的一种写法：身份来自令牌还是来自请求头，
 *    取决于后端过滤器的顺序 —— 一个「登录成功了但看到的是另一个租户的数据」
 *    的缺陷会在这里诞生，而且两边都不报错。
 *
 * ⇒ 登录全面接上之后，这个函数连同 `shared/src/http/devTenant.ts`
 *    与 `components/DevTenantBar.vue` 一起删。
 */
function authHeaders(): Record<string, string> {
  const bearer = bearerHeader()
  if (Object.keys(bearer).length > 0) return bearer
  // 🔴 `import.meta.env.DEV` 是构建期常量 ⇒ 生产构建里这一整支被摇掉，
  //    产物里连 devTenantHeaders 的引用都没有（实测 dist 里 grep 不到 X-Tenant-Id）
  return import.meta.env.DEV ? devTenantHeaders() : {}
}

const delegate = createHttpClient({ extraHeaders: authHeaders })

// 刷新端点不能带过期的 Authorization，否则会在到达 AuthController 前被拦成 401。
const anonymousClient = createHttpClient()
const REFRESH_TOKEN_INVALID = 99407

interface TokenPairResponse {
  accessToken: string
  accessExpiresIn: number
  refreshToken: string
  refreshExpiresIn: number
  userId: WireAuthId
  subjectType: string
  tenantId: WireAuthId
  subjectId: WireAuthId
  mustChangePassword: boolean
}

/** 刷新令牌一次即轮换；同一屏的并发请求必须共用一次刷新。 */
let inflightRefresh: { owner: RequestOwner; token: string; promise: Promise<boolean> } | null = null

export async function refreshAccessToken(): Promise<boolean> {
  const owner = ownerNow()
  const refreshToken = refreshTokenOrNull()
  if (!refreshToken) return false
  if (inflightRefresh && inflightRefresh.token === refreshToken && sameOwner(inflightRefresh.owner)) {
    return inflightRefresh.promise
  }

  const rotate = async (): Promise<boolean> => {
    requireOwner(owner);
    // Another same-origin tab may have rotated the token while this tab waited for the lock.
    if (refreshTokenOrNull() !== refreshToken) return hasSession() && !accessTokenExpiringWithin(0);
    try {
      const pair = await anonymousClient.post<TokenPairResponse>('/auth/v1/refresh', {
        refreshToken,
        audience: 'WEB',
      })
      if (!sameOwner(owner)) return false
      if (refreshTokenOrNull() !== refreshToken) return hasSession() && !accessTokenExpiringWithin(0)
      const session = {
        userId: normalizeAuthId(pair.userId, 'userId'),
        subjectType: pair.subjectType,
        tenantId: normalizeAuthId(pair.tenantId, 'tenantId'),
        subjectId: normalizeAuthId(pair.subjectId, 'subjectId'),
        mustChangePassword: pair.mustChangePassword,
      }
      if (JSON.stringify([session.subjectType, session.userId, session.tenantId, session.subjectId]) !== owner.identity) {
        throw new SessionChangedError('刷新响应的登录主体与原会话不一致')
      }
      savingRefreshToken = pair.refreshToken
      try {
        saveTokens({
          accessToken: pair.accessToken,
          accessExpiresIn: pair.accessExpiresIn,
          refreshToken: pair.refreshToken,
          refreshExpiresIn: pair.refreshExpiresIn,
          session,
        })
      } finally {
        savingRefreshToken = null
      }
      return true
    } catch (error) {
      if (isApiError(error)
        && (error.code === ErrorCodes.UNAUTHORIZED || error.code === REFRESH_TOKEN_INVALID)) {
        if (sameOwner(owner) && refreshTokenOrNull() !== refreshToken) return hasSession() && !accessTokenExpiringWithin(0)
        if (sameOwner(owner) && refreshTokenOrNull() === refreshToken) clearTokens()
        return false
      }
      // 网络/网关故障不能被翻译成“退出登录”；保留会话，恢复网络后还能续期。
      throw error
    }
  }
  const promise = (async (): Promise<boolean> => await (typeof navigator !== 'undefined' && navigator.locks?.request
    ? navigator.locks.request('jianjie-web-session-refresh', { mode: 'exclusive' }, rotate)
    : rotate()))()
  inflightRefresh = { owner, token: refreshToken, promise }
  void promise.then(
    () => { if (inflightRefresh?.promise === promise) inflightRefresh = null },
    () => { if (inflightRefresh?.promise === promise) inflightRefresh = null },
  )
  return promise
}

async function ensureFreshAccessToken(): Promise<void> {
  if (!hasSession() || !accessTokenExpiringWithin()) return
  try {
    await refreshAccessToken()
  } catch (error) {
    if (error instanceof SessionChangedError) throw error
    // 提前 60 秒刷新失败时，当前 access 可能仍有效；让本次请求得到服务端权威判定。
  }
}

/** Legacy envelope and binary clients share the same refresh owner; this never replays their requests. */
export async function freshBearerHeader(): Promise<Record<string, string>> {
  const owner = ownerNow()
  await ensureFreshAccessToken()
  requireOwner(owner)
  return bearerHeader()
}

function accessHeaderValue(): string | undefined {
  return bearerHeader().Authorization
}

function isUnauthorized(error: unknown): boolean {
  return isApiError(error)
    && (error.code === ErrorCodes.UNAUTHORIZED || error.code === REFRESH_TOKEN_INVALID)
}

function redirectToLogin(owner: RequestOwner): void {
  // An invalid refresh may already have cleared the original session. Never clear a newer one.
  if (identityOf() !== null) requireOwner(owner)
  if (sameOwner(owner)) clearTokens()
  if (sessionStorage.getItem(UNAUTHORIZED_REDIRECT_KEY) !== '1'
    && window.location.pathname !== '/login') {
    sessionStorage.setItem(UNAUTHORIZED_REDIRECT_KEY, '1')
    const redirect = `${window.location.pathname}${window.location.search}${window.location.hash}`
    void router.replace({ name: 'login', query: { redirect } }).catch(() => {
      sessionStorage.removeItem(UNAUTHORIZED_REDIRECT_KEY)
    })
  }
}

async function ownedGet<T>(owner: RequestOwner, path: string, options?: RequestOptions): Promise<T> {
  try {
    return await delegate.get<T>(path, options)
  } finally {
    requireOwner(owner)
  }
}

async function get<T>(path: string, options?: RequestOptions): Promise<T> {
  const owner = ownerNow()
  await ensureFreshAccessToken()
  requireOwner(owner)
  const sentAccess = accessHeaderValue()
  try {
    return await ownedGet<T>(owner, path, options)
  } catch (error) {
    requireOwner(owner)
    if (!isUnauthorized(error)) throw error

    // 另一请求已经完成轮换时，这个 401 属于旧 access 的迟到响应；直接用新 access
    // 重放安全的 GET，绝不能把新会话清掉或再消费一次 refresh token。
    if (sentAccess && accessHeaderValue() && sentAccess !== accessHeaderValue()) {
      return ownedGet<T>(owner, path, options)
    }
    let refreshed = false
    try {
      refreshed = await refreshAccessToken()
    } catch (refreshError) {
      requireOwner(owner)
      if (refreshError instanceof SessionChangedError) throw refreshError
      // 网络刷新失败保留会话；当前请求仍按原始 401 失败，不伪装成登出。
      throw error
    }
    if (refreshed) {
      requireOwner(owner)
      return ownedGet<T>(owner, path, options)
    }
    if (identityOf() !== null) requireOwner(owner)
    if (!(sentAccess && accessHeaderValue() && sentAccess !== accessHeaderValue())) redirectToLogin(owner)
    throw error
  }
}

async function write<T>(
  request: () => Promise<T>,
  sentAccess: string | undefined,
  owner: RequestOwner,
): Promise<T> {
  try {
    const response = await request()
    requireOwner(owner)
    return response
  } catch (error) {
    requireOwner(owner)
    if (!isUnauthorized(error)) throw error

    // 写请求绝不自动重放。迟到 401 若已经被另一请求刷新，只保留新会话并把本次失败
    // 原样交给页面；否则尝试刷新一次，为用户下一次明确操作准备好新 access。
    if (!(sentAccess && accessHeaderValue() && sentAccess !== accessHeaderValue())) {
      try {
        if (!(await refreshAccessToken())) {
          if (identityOf() !== null) requireOwner(owner)
          if (!(sentAccess && accessHeaderValue() && sentAccess !== accessHeaderValue())) redirectToLogin(owner)
        }
      } catch (refreshError) {
        if (identityOf() !== null) requireOwner(owner)
        if (refreshError instanceof SessionChangedError) throw refreshError
        // 网络错误不能清会话。
      }
    }
    throw error
  }
}

export const http = {
  get,
  post: async <T>(path: string, body?: unknown, options?: RequestOptions) => {
    const owner = ownerNow()
    await ensureFreshAccessToken()
    requireOwner(owner)
    const sentAccess = accessHeaderValue()
    return write(() => delegate.post<T>(path, body, options), sentAccess, owner)
  },
  put: async <T>(path: string, body?: unknown, options?: RequestOptions) => {
    const owner = ownerNow()
    await ensureFreshAccessToken()
    requireOwner(owner)
    const sentAccess = accessHeaderValue()
    return write(() => delegate.put<T>(path, body, options), sentAccess, owner)
  },
  /** DELETE 与 put 走同一条写路径：迟到 401 不自动重放，交给页面按结果处理 */
  del: async <T>(path: string, options?: RequestOptions) => {
    const owner = ownerNow()
    await ensureFreshAccessToken()
    requireOwner(owner)
    const sentAccess = accessHeaderValue()
    return write(() => delegate.del<T>(path, options), sentAccess, owner)
  },
}

/**
 * 会真的去调平台的接口要单独放宽超时。
 *
 * 🔴 绑定与凭据体检都会**真实出站**到 Ozon：默认 15 秒会在对端慢的时候把请求切掉，
 * 而切掉的表现是前端报「超时」+ 后端其实**已经把店铺写进去了**。
 */
export const OUTBOUND_TIMEOUT_MS = 45_000
